import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Modal from '../shared/Modal';
import ToggleSwitch from '../shared/ToggleSwitch';
import { cleanObject } from '../CleanObject';
import TextInput from '../InputComponent/TextInput';
import SelectInput from '../InputComponent/SelectInput';
import TextAreaInput from '../InputComponent/TextAreaInput';

const PlanFormModal = ({ isOpen, onClose, onSave, plan }) => {
    const initialFormState = {
        id: '',
        name: '',
        duration: '1',
        price: "100.000",
        access_type: 'month',
        features: '',
        session_duration: null,
        status: 'active',
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        if (plan) {
            setFormData({
                id: plan.id || '',
                name: plan.name || '',
                duration: plan.duration || '30',
                price: plan.price || 0.000,
                access_type: plan.access_type || 'month',
                features: plan.features || '',
                ...(plan.session_duration && { session_duration: plan.session_duration }),
                status: plan.status || 'active',
            });
        } else {
            setFormData(initialFormState);
        }
    }, [plan, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "price") {
            // Allow only numbers + one dot
            let cleaned = value.replace(/[^0-9.]/g, "");

            // Prevent more than one dot
            const parts = cleaned.split(".");
            if (parts.length > 2) return;

            // If decimal exists, limit to 3 digits
            if (parts[1] && parts[1].length > 3) {
                cleaned = parts[0] + "." + parts[1].slice(0, 3);
            }

            setFormData({ ...formData, price: cleaned });
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleToggle = (checked) => {
        setFormData(prev => ({ ...prev, status: checked ? 'active' : 'inactive' }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name) {
            toast.error("Plan name is required.");
            return;
        }

        if (!formData.price || Number(formData.price) <= 0) {
            toast.error("Price must be greater than zero.");
            return;
        }

        if (!formData.duration) {
            toast.error("Duration is required.");
            return;
        }

        if (formData.access_type === "session" && !formData.session_duration) {
            toast.error("Session duration is required for session-based plans.");
            return;
        }
        console.log("save formdata ", cleanObject(formData));
        onSave(cleanObject(formData));
    };

    const isEditing = !!plan;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Plan' : 'Add New Plan'}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <TextInput label={"Plan Name"} type="text" name="name" value={formData.name} onChange={handleChange} isRequired={true} />
                <SelectInput label={"Plan Type"} name="access_type" value={formData.access_type} onChange={handleChange} isRequired={true}>
                    <option value={'month'}>Month Based</option>
                    <option value={'session'}>Session-based</option>
                </SelectInput>
                <TextInput type='number' name="duration" value={formData.duration} onChange={handleChange} label={`Duration (${formData.access_type == 'session' ? 'Days' : 'Month'})`} />
                {formData.access_type === "session" && (
                    <TextInput
                        type="number"
                        name="session_duration"
                        value={formData.session_duration ?? ""}
                        onChange={handleChange}
                        label="Session Duration (hours)"
                        isRequired={true}
                    />
                )}
                <TextInput label={"Price (OMR)"} type="number" isPriceInput={true} name="price" value={formData.price} onChange={handleChange} min="0" step="0.001" isRequired={true}/>
                <TextAreaInput label={"Features (comma-separated)"} name="features" value={formData.features} onChange={handleChange} rows="3"  />

                <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <ToggleSwitch enabled={formData.status === 'active'} setEnabled={handleToggle} />
                </div>

                <div className="pt-4 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cancel</button>
                    <button type="submit" className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-red-600">Save Plan</button>
                </div>
            </form>
        </Modal>
    );
};

export default PlanFormModal;
